import { APIStatus } from "@/api/APIStatus.type";
import { getStats } from "@/api/privateApi";
import {
  computeNewPlayerRecs as computeNewPlayerRecsApi,
  getUserRecommendations,
  markRecommendationsAsViewed,
} from "@/api/privateRequests/user/userRecommendations";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  Manager,
  SquadBuilderPlayerType,
  calculateMetaRatingAndChemistry,
} from "../squadBuilder/squadBuilderUtils";
import { IRootState } from "../store";

export type IndividualUpgrades = {
  upgradeDelta: number;
  type: UpgradeType;
};

type ActiveSquadPlayer = {
  chemstyleId: number;
  playerRoleId: string;
  position: string;
  clubId: number;
  leagueId: number;
  countryId: number;
  versionId: number;
  metaRatings: {
    metaRating: number;
    playerRoleId: number;
    chemstyleId: number;
    chemistry: number;
  }[];
  possiblePositions: string[];
};

export interface ClubStats {
  clubValue: number;
  coins: number;
  sbcFodder: number;
  potentialCoins: number;
  transferList: number;
  rank: number;
  playersCount: number;
  evolvedPlayersCount: number;
  clubName: string;
  clubBadgeId: number;
  lastImportedAt: number;
  squadRecsLastUpdatedAt: number;
  squadChangedSinceLastRecs: boolean;
  activeSquad: {
    name: string;
    formation: string;
    players: ActiveSquadPlayer[];
    manager: Manager;
  };
}

export type UpgradeType =
  | "player-meta-upgrade"
  | "team-meta-upgrade"
  | "player-chemistry-upgrade"
  | "team-chemistry-upgrade";

export type RecommendationType =
  | "market"
  | "sbc"
  | "objective"
  | "club"
  | "evolution";

export type RecommendationEffortType = "low" | "medium" | "high";

type RecommendationPlayer = {
  assetId: number;
  resourceId: number;
  clubId: number;
  countryId: number;
  leagueId: number;
  playerRoleId: number;
  hasDynamicImage: boolean;
  metalId: number;
  playStylesPlus: string[];
  playerUrl: string;
  rating: number;
  versionId: number;
  cardName: string;
  position: string;
  evoPathId?: number;
  evolutionPath?: number[];
  evolutionCost?: number;
  price?: number;
  sbcPrice?: number;
  canEvolve?: boolean;
};

export type UpgradeLevel = 1 | 2 | 3;
export interface Recommendation {
  id: string;
  title: string;
  upgradeLevel: UpgradeLevel;
  individualUpgrades: IndividualUpgrades[];
  type: RecommendationType;
  isNew: boolean;
  effort?: RecommendationEffortType;
  oldPlayer?: RecommendationPlayer;
  newPlayer?: RecommendationPlayer;
  expirationDate?: number;
  price?: number;
}

type MyClubState = {
  recommendations: Recommendation[];
  clubStats: ClubStats;
  fetchRecommendationStatus?: APIStatus;
  computeRecommendationStatus?: APIStatus;
  fetchClubStatsStatus?: APIStatus;
};

const initialState: MyClubState = {
  recommendations: [],
  clubStats: {
    clubName: "",
    clubBadgeId: 0,
    lastImportedAt: 0,
    coins: 0,
    clubValue: 0,
    sbcFodder: 0,
    potentialCoins: 0,
    transferList: 0,
    rank: 0,
    playersCount: 0,
    evolvedPlayersCount: 0,
    squadRecsLastUpdatedAt: 0,
    squadChangedSinceLastRecs: false,
    activeSquad: {
      name: "",
      formation: "f442",
      players: [],
      manager: {},
    },
  },
  fetchRecommendationStatus: "idle",
  computeRecommendationStatus: "idle",
  fetchClubStatsStatus: "idle",
};

export const fetchRecommendations = createAsyncThunk(
  "my-club/get-recommendations",
  async (_, { dispatch, getState }) => {
    const recommendations = await getUserRecommendations();
    const state = getState() as IRootState;
    const formattedRecommendations = [];
    let anyNewRecommendations = false;
    recommendations.forEach((recommendation) => {
      if (recommendation.isNew) {
        anyNewRecommendations = true;
      }
      formattedRecommendations.push(recommendation);
    });
    dispatch(setRecommendations(formattedRecommendations));
    if (anyNewRecommendations && state.userReducer.user.uuid) {
      await markRecommendationsAsViewed();
    }
  },
);

export const fetchClubStats = createAsyncThunk(
  "my-club/fetch-club-stats",
  async () => getStats(),
);

export const computeNewPlayerRecs = createAsyncThunk(
  "my-club/compute-new-player-recs",
  async () => {
    const newPlayerRecs = await computeNewPlayerRecsApi();
    return newPlayerRecs;
  },
);

const myClubSlice = createSlice({
  name: "myClub",
  initialState,
  reducers: {
    setRecommendations(state, action: PayloadAction<Recommendation[]>) {
      state.recommendations = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRecommendations.pending, (state) => {
      state.fetchRecommendationStatus = "pending";
    });
    builder.addCase(fetchRecommendations.fulfilled, (state) => {
      state.fetchRecommendationStatus = "fulfilled";
    });
    builder.addCase(fetchRecommendations.rejected, (state) => {
      state.fetchRecommendationStatus = "rejected";
    });
    builder.addCase(computeNewPlayerRecs.pending, (state) => {
      state.computeRecommendationStatus = "pending";
    });
    builder
      .addCase(
        computeNewPlayerRecs.fulfilled,
        (state, action: PayloadAction<Recommendation[]>) => {
          state.computeRecommendationStatus = "fulfilled";
          state.recommendations = action.payload;
          state.clubStats.squadRecsLastUpdatedAt = new Date().getTime();
          state.clubStats.squadChangedSinceLastRecs = false;
        },
      )
      .addCase(computeNewPlayerRecs.rejected, (state) => {
        state.computeRecommendationStatus = "rejected";
      })
      .addCase(fetchClubStats.fulfilled, (state, action) => {
        state.clubStats = action.payload;
        state.fetchClubStatsStatus = "fulfilled";
      })
      .addCase(fetchClubStats.pending, (state) => {
        state.fetchClubStatsStatus = "pending";
      })
      .addCase(fetchClubStats.rejected, (state) => {
        state.fetchClubStatsStatus = "rejected";
      });
  },
});

export const { setRecommendations } = myClubSlice.actions;

export const myClubSelector = (state: IRootState) => state.myClubReducer;

export const computeRecommendationStatusSelector = (state: IRootState) =>
  state.myClubReducer.computeRecommendationStatus;

export const recommendationsSelector = (state: IRootState) =>
  state.myClubReducer.recommendations;

export const activeSquadMetaRatingSelector = (state: IRootState) => {
  const players = state.myClubReducer.clubStats.activeSquad?.players;

  if (
    !players ||
    players?.length === 0 ||
    !state.loadedContentReducer.chemistryProfiles
  ) {
    return 0;
  }

  const { teamMetaRating } = calculateMetaRatingAndChemistry(
    players as unknown as SquadBuilderPlayerType[],
    state.myClubReducer.clubStats.activeSquad.manager,
    state.myClubReducer.clubStats.activeSquad.formation,
    state.loadedContentReducer.chemistryProfiles,
  );

  return teamMetaRating;
};

export const clubStatsSelector = (state: IRootState) =>
  state.myClubReducer.clubStats;

export default myClubSlice.reducer;
